import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Card, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, Tabs, Tab, Button, Chip, IconButton, Divider,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentIcon from '@mui/icons-material/Payment';
import StarIcon from '@mui/icons-material/Star';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

// Map notification type → icon + color
const typeConfig = {
  case: { icon: AssignmentIcon, color: '#1B365D' },
  lead: { icon: PersonAddIcon, color: '#00B894' },
  document: { icon: DescriptionIcon, color: '#F57F17' },
  approval: { icon: CheckCircleIcon, color: '#2E7D32' },
  rejection: { icon: CancelIcon, color: '#C62828' },
  commission: { icon: PaymentIcon, color: '#00B894' },
  token: { icon: StarIcon, color: '#FFD700' },
  redemption: { icon: StarIcon, color: '#7B1FA2' },
  system: { icon: NotificationsIcon, color: '#546E7A' },
};

const relativeTime = (isoString) => {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  return new Date(isoString).toLocaleDateString();
};

export default function NotificationCenter() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // Live notifications from db
  const items = useMemo(() => {
    if (!user?.id) return [];
    const all = user.role === 'admin'
      ? db.notifications.getAll()
      : db.notifications.getByUserId(user.id);
    return all
      .slice()
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [user, refreshKey]);

  const unreadCount = items.filter((n) => !n.read).length;

  const filtered = useMemo(() => {
    if (tab === 0) return items;
    if (tab === 1) return items.filter((n) => !n.read);
    if (tab === 2) return items.filter((n) => ['case', 'lead', 'document'].includes(n.type));
    return items.filter((n) => ['commission', 'token', 'redemption'].includes(n.type));
  }, [items, tab]);

  const markAllRead = () => {
    items.filter((n) => !n.read).forEach((n) => db.notifications.markRead(n.id));
    setRefreshKey((k) => k + 1);
  };

  const markRead = (id) => {
    db.notifications.markRead(id);
    setRefreshKey((k) => k + 1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D' }}>Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={() => setRefreshKey((k) => k + 1)}
            sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
          <Button startIcon={<MarkEmailReadIcon />} onClick={markAllRead}
            disabled={unreadCount === 0}
            sx={{ textTransform: 'none' }}>
            Mark all as read
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            All
            {unreadCount > 0 && (
              <Chip label={unreadCount} size="small"
                sx={{ height: 20, backgroundColor: '#C62828', color: 'white', fontWeight: 700, fontSize: '0.65rem' }} />
            )}
          </Box>
        } />
        <Tab label="Unread" />
        <Tab label="Cases & Leads" />
        <Tab label="Rewards & Commission" />
      </Tabs>

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <List sx={{ p: 0 }}>
          {filtered.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: '#C4CDD5', mb: 1 }} />
              <Typography color="text.secondary">No notifications</Typography>
            </Box>
          ) : (
            filtered.map((notif, idx) => {
              const cfg = typeConfig[notif.type] || typeConfig.system;
              const Icon = cfg.icon;
              return (
                <React.Fragment key={notif.id}>
                  <ListItem
                    onClick={() => markRead(notif.id)}
                    sx={{
                      px: 3, py: 2, cursor: 'pointer',
                      backgroundColor: notif.read ? 'transparent' : 'rgba(27,54,93,0.04)',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                    }}
                    secondaryAction={
                      !notif.read && (
                        <Chip label="New" size="small" sx={{ backgroundColor: '#C62828', color: 'white', fontWeight: 700, fontSize: '0.6rem', height: 18 }} />
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: `${cfg.color}15`, color: cfg.color, width: 40, height: 40 }}>
                        <Icon sx={{ fontSize: 22 }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: notif.read ? 500 : 700, color: '#1B365D' }}>
                          {notif.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
                            {notif.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {relativeTime(notif.createdAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {idx < filtered.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })
          )}
        </List>
      </Card>
    </Box>
  );
}
